import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatDate, getWeekday } from '@/lib/date';
import { getStatusMeta, recordStatusMap } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';
import Modal from '@/components/base/Modal';
import RecordModal from './components/RecordModal';

interface RecordRow {
  id: string;
  title: string;
  lesson_id: string | null;
  group_id: string | null;
  description: string | null;
  video_url: string | null;
  alt_url: string | null;
  published: boolean;
  visible_to_students: boolean;
  date: string | null;
  views: number;
  duration: string | null;
  created_at: string;
}

interface LessonInfo {
  id: string;
  title: string;
  date: string | null;
  start_time: string | null;
  status: string | null;
  group_id: string | null;
}

interface GroupInfo {
  id: string;
  name: string;
  color: string | null;
}

export default function Kayitlar() {
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [lessons, setLessons] = useState<LessonInfo[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterPublished, setFilterPublished] = useState<string>('hepsi');
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<RecordRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RecordRow | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: recordsData, error: recordsError },
        { data: lessonsData, error: lessonsError },
        { data: groupsData, error: groupsError },
      ] = await Promise.all([
        supabase.from('zehrahoca_lesson_records').select('*').order('date', { ascending: false }),
        supabase.from('zehrahoca_lessons').select('id, title, date, start_time, status, group_id').order('date', { ascending: false }),
        supabase.from('zehrahoca_groups').select('id, name, color').eq('active', true),
      ]);

      if (recordsError) throw recordsError;
      if (lessonsError) throw lessonsError;
      if (groupsError) throw groupsError;

      setRecords((recordsData ?? []) as RecordRow[]);
      setLessons((lessonsData ?? []) as LessonInfo[]);
      setGroups((groupsData ?? []) as GroupInfo[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:records-changed', handleChange);
    return () => window.removeEventListener('zehrahoca:records-changed', handleChange);
  }, [fetchAll]);

  const lessonMap = useMemo(() => {
    const map: Record<string, LessonInfo> = {};
    lessons.forEach((l) => { map[l.id] = l; });
    return map;
  }, [lessons]);

  const groupMap = useMemo(() => {
    const map: Record<string, GroupInfo> = {};
    groups.forEach((g) => { map[g.id] = g; });
    return map;
  }, [groups]);

  const filtered = useMemo(() => {
    let list = records;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.title ?? '').toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q) ||
        (lessonMap[r.lesson_id ?? '']?.title ?? '').toLowerCase().includes(q) ||
        (groupMap[r.group_id ?? '']?.name ?? '').toLowerCase().includes(q),
      );
    }
    if (filterPublished === 'yayin') {
      list = list.filter((r) => r.published);
    } else if (filterPublished === 'taslak') {
      list = list.filter((r) => !r.published);
    }
    return list;
  }, [records, search, filterPublished, lessonMap, groupMap]);

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      const { error: delError } = await supabase.from('zehrahoca_lesson_records').delete().eq('id', confirmDelete.id);
      if (delError) throw delError;
      setConfirmDelete(null);
      window.dispatchEvent(new Event('zehrahoca:records-changed'));
    } catch (err) {
      setError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setConfirmDelete(null);
    }
  }

  function handleEdit(r: RecordRow) {
    setEditRecord(r);
    setModalOpen(true);
  }

  function handleNew() {
    setEditRecord(null);
    setModalOpen(true);
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Kayıtlar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Kayıtlar yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={fetchAll} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ders Kayıtları</h1>
          <p className="mt-1 text-sm text-foreground-600">Ders kayıtlarını yönetin ve öğrencilerin erişimini düzenleyin.</p>
        </div>
        <button type="button" onClick={handleNew} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
          <i className="ri-add-line text-base" /> Yeni Kayıt Ekle
        </button>
      </div>

      {/* Filtreler */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kayıt, ders veya grup ara..."
            className="w-full rounded-lg border border-background-300 bg-white py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
          {[
            { key: 'hepsi', label: 'Tümü' },
            { key: 'yayin', label: 'Yayında' },
            { key: 'taslak', label: 'Taslak' },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilterPublished(f.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition cursor-pointer ${
                filterPublished === f.key ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tablo */}
      <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-background-50 text-foreground-700">
              <tr>
                <th className="px-4 py-3 font-semibold">Kayıt Başlığı</th>
                <th className="px-4 py-3 font-semibold">Ders</th>
                <th className="px-4 py-3 font-semibold">Grup</th>
                <th className="px-4 py-3 font-semibold">Tarih</th>
                <th className="px-4 py-3 font-semibold">Süre</th>
                <th className="px-4 py-3 font-semibold">Durum</th>
                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-foreground-500">
                    Kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const lesson = lessonMap[r.lesson_id ?? ''];
                  const group = groupMap[r.group_id ?? ''];
                  const status = getStatusMeta(r.published ? 'yayinda' : 'taslak', recordStatusMap);
                  return (
                    <tr key={r.id} className="hover:bg-background-50/50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                            <i className="ri-vidicon-line text-lg" />
                          </div>
                          <div>
                            <p className="font-semibold text-foreground-950">{r.title}</p>
                            {r.visible_to_students && (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                                <i className="ri-eye-line" /> Öğrenci görüyor
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-700">
                        {lesson ? (
                          <span>
                            {lesson.title}
                            <span className="ml-1 text-xs text-foreground-400">
                              {lesson.date ? `(${formatDate(lesson.date)})` : ''}
                            </span>
                          </span>
                        ) : (
                          <span className="text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {group ? (
                          <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold" style={{ backgroundColor: `${group.color ?? '#94a3b8'}20`, color: group.color ?? '#94a3b8' }}>
                            <i className="ri-team-line" /> {group.name}
                          </span>
                        ) : (
                          <span className="text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-foreground-700">
                        {r.date ? `${formatDate(r.date)} · ${getWeekday(r.date)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-foreground-700">{r.duration ?? '—'}</td>
                      <td className="px-4 py-3"><StatusBadge meta={status} /></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {r.video_url && (
                            <a
                              href={r.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100"
                              title="Videoyu Aç"
                            >
                              <i className="ri-play-circle-line text-base" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => handleEdit(r)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200"
                            title="Düzenle"
                          >
                            <i className="ri-edit-line text-base" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(r)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100"
                            title="Sil"
                          >
                            <i className="ri-delete-bin-line text-base" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RecordModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        record={editRecord}
        lessons={lessons}
        groups={groups}
      />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Silmek istediğinize emin misiniz?"
        subtitle="Bu işlem ilgili kaydı panelden kaldırır. Bağlantılı içerikler etkilenebilir."
      >
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{confirmDelete?.title}</span> kaydı kalıcı olarak silinecek.
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap"
            >
              Sil
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}